'use client';
import Icon from '../Icon';
import { plannerPlans } from '@/lib/vster/data';

interface Props {
  navigate: (s: string) => void;
  plannerTarget: number;
  setPlannerTarget: (n: number) => void;
}

export default function PlannerScreen({ navigate, plannerTarget, setPlannerTarget }: Props) {
  const idx = Math.min(plannerTarget, plannerPlans.length - 1);
  const plan = plannerPlans[idx];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#737685', marginBottom: 10 }}>
          <button onClick={() => navigate('kanban')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#737685', fontFamily: 'inherit', fontSize: 12, padding: 0 }}>트래킹 보드</button>
          <Icon name="chevron_right" size={15} />
          <span style={{ color: '#051a3e', fontWeight: 600 }}>마감 역산 플래너</span>
        </div>
        <h1 className="vh1" style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-.015em', margin: '0 0 4px' }}>마감 역산 플래너</h1>
        <p style={{ fontSize: 16, color: '#434654', margin: 0 }}>대상 공고를 선택하면 마감일 기준으로 할 일을 역산해 배치합니다.</p>
      </div>

      {/* 공고 선택 */}
      <div style={{ background: '#fff', border: '1px solid #e4e8f6', borderRadius: 16, padding: '18px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', boxShadow: '0 2px 12px rgba(5,26,62,.03)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 700, color: '#051a3e', flexShrink: 0 }}>
          <Icon name="flag" size={20} color="#003d9b" fill={1} />대상 공고
        </span>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <select
            value={idx}
            onChange={e => setPlannerTarget(Number(e.target.value))}
            style={{ width: '100%', padding: '11px 40px 11px 14px', border: '1px solid #c3c6d6', borderRadius: 10, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: '#051a3e', background: '#f7f8ff', cursor: 'pointer', outline: 'none', appearance: 'none' }}
          >
            {plannerPlans.map((p, i) => (
              <option key={p.id} value={i}>{p.title} · 마감 {p.deadline} ({p.dday})</option>
            ))}
          </select>
          <Icon name="expand_more" size={22} color="#737685" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        </div>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#737685', flexShrink: 0 }}>
          <Icon name="corporate_fare" size={16} color="#737685" />{plan.org}
        </span>
      </div>

      {/* 통계 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: '제출 마감일', value: plan.deadline },
          { label: '남은 기간', value: plan.dday, valueColor: plan.ddColor },
          { label: '진행률', value: plan.progressLabel },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e4e8f6', borderRadius: 14, padding: 16 }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#737685', marginBottom: 5 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.valueColor }}>{s.value}</div>
          </div>
        ))}
        <div style={{ background: '#fff', border: '1px solid #e4e8f6', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#737685' }}>{plan.progressPct}</div>
          <div style={{ height: 8, borderRadius: 999, background: '#e9edff', overflow: 'hidden' }}>
            <div style={{ width: plan.progressPct, height: '100%', background: 'linear-gradient(90deg,#003d9b,#00687b)' }} />
          </div>
        </div>
      </div>

      <div className="vstack-sm" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(260px,300px)', gap: 24, alignItems: 'start' }}>
        {/* 타임라인 */}
        <div style={{ background: '#fff', border: '1px solid #e4e8f6', borderRadius: 16, padding: '26px 24px', boxShadow: '0 2px 12px rgba(5,26,62,.03)' }}>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 15, top: 14, bottom: 14, width: 2, background: '#e4e8f6' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {plan.milestones.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '10px 0', position: 'relative' }}>
                  <span style={{ width: 32, height: 32, borderRadius: '50%', background: m.dotBg, border: m.dotBorder, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                    {m.icon && <Icon name={m.icon} size={18} color={m.iconColor} fill={1} />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: m.labelWeight as any, color: m.labelColor }}>{m.label}</div>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#737685', marginTop: 2 }}>{m.dday} · {m.date}</div>
                    </div>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, fontWeight: 600, color: m.statusColor, flexShrink: 0 }}>{m.statusLabel}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button style={{ marginTop: 14, width: '100%', background: 'none', border: '1.5px dashed #c3c6d6', borderRadius: 11, padding: 13, cursor: 'pointer', color: '#003d9b', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Icon name="add" size={19} />커스텀 할 일 추가
          </button>
        </div>

        {/* 사이드바 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 캘린더 연동 */}
          <div style={{ background: '#fff', border: '1px solid #e4e8f6', borderRadius: 16, padding: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>캘린더 연동</h3>
            <p style={{ fontSize: 13, color: '#737685', margin: '0 0 14px', lineHeight: 1.5 }}>마일스톤을 캘린더 일정으로 내보냅니다.</p>
            <button style={{ width: '100%', background: '#003d9b', color: '#fff', border: 'none', cursor: 'pointer', padding: 11, borderRadius: 10, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, marginBottom: 8 }}>
              <Icon name="event" size={18} color="#fff" />Google Calendar 연동
            </button>
            <button style={{ width: '100%', background: '#e9edff', color: '#003d9b', border: 'none', cursor: 'pointer', padding: 11, borderRadius: 10, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              <Icon name="download" size={18} />iCal(.ics) 다운로드
            </button>
          </div>
          {/* 경고 */}
          <div style={{ background: '#fff5f5', border: '1px solid #ffdad6', borderRadius: 16, padding: 18, display: 'flex', gap: 11, alignItems: 'flex-start' }}>
            <Icon name="warning" size={22} color="#ba1a1a" fill={1} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#93000a', marginBottom: 3 }}>서류 준비 단계 임박</div>
              <div style={{ fontSize: 13, color: '#93000a', lineHeight: 1.5 }}>기업 현황·재무 서류 준비가 오늘 시작 예정입니다. 늦어지면 이후 일정이 함께 밀립니다.</div>
            </div>
          </div>
          {/* AI 추천 */}
          <div style={{ background: '#003d9b', borderRadius: 16, padding: 18, color: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Icon name="auto_awesome" size={20} color="#50dcff" />
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>AI 추천 템플릿</h3>
            </div>
            <p style={{ fontSize: 13, color: '#dae2ff', margin: 0, lineHeight: 1.55 }}>사업화 유형 공고 기준으로 8단계 일정을 자동 생성했습니다. R&D 유형으로 전환할 수 있습니다.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
